import React, { ReactNode } from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { rem } from 'polished';
import { mixins, colors, spaces } from '../../core/styles/theme/default';
import { Burrito, Grid, GridItem } from '../../helpers';
import {
  Accordion,
  AccordionItem,
  AccordionItemTrigger,
  AccordionItemContent,
} from '../../components/Accordion';
import { ModuleHeader } from '../../primitives';
import ContactBox from '../../components/ContactBox/ContactBox';
import { LayoutProp } from '~/core/styles/theme/layout';

const StyledFaq = styled.div(
  ({ backgroundColor }: { backgroundColor?: string }) => css`
    ${backgroundColor &&
    css`
      position: relative;
      ${mixins.responsiveStyles({
        'padding-top': {
          zero: rem(20),
          medium: rem(64),
          large: rem(80),
        },
      })}

      &::before {
        content: '';
        width: 100vw;
        position: absolute;
        top: 0;
        background-color: ${backgroundColor};

        ${mixins.responsiveStyles({
          left: {
            zero: rem(-16),
            medium: rem(-24),
            large: rem(-48),
            wide: `calc(-1 * ((100vw - ${rem(1088)} ) / 2))`,
            ultra: `calc(-1 * ((100vw - ${rem(1260)} ) / 2))`,
          },
          height: {
            zero: '100%',
            medium: `calc(100% - ${rem(146)})`,
          },
        })}
      }
    `};
  `,
);

const StyledGrid = styled(Grid)(
  () => css`
    > div {
      ${mixins.MQMax('large')} {
        flex-grow: 1;
      }
    }
  `,
);

const StyledBurrito = styled(Burrito)(
  ({ hasBackground }: any) => css`
    ${hasBackground
      ? css`
          & > .offsetBackground {
            background-color: ${colors.blue100};
          }

          &::after {
            content: '';
            position: absolute;
            background: ${colors.blue100};
            top: 100%;
            left: -${rem(spaces.epsilon)};
            right: -${rem(spaces.epsilon)};
            height: 26%;

            ${mixins.MQ('large')} {
              top: 0;
              bottom: 0;
              left: 100%;
              right: auto;
              width: 58%;
              height: auto;
            }

            ${mixins.MQ('ultra')} {
              width: 55%;
            }
          }
        `
      : css`
          margin-top: 0 !important;
          margin-bottom: 0 !important;
        `};
  `,
);

const StyledAccordionItem = styled(AccordionItem)(
  ({
    hasBackground,
    variant,
  }: {
    hasBackground?: boolean;
    variant?: 'light';
  }) => css`
    ${hasBackground &&
    css`
      border-color: #d1d9e4;
    `};

    ${variant === 'light' &&
    css`
      border-color: ${colors.white};
      color: ${colors.white};

      a {
        font-size: inherit;
        line-height: inherit;

        &,
        &:hover,
        &:focus,
        &:active,
        &:visited {
          color: currentColor;
        }
      }

      button {
        color: currentColor;

        svg path {
          stroke: currentColor;
        }
      }
    `};
  `,
);

const Faq = (faqProps: FaqProps) => {
  if (!faqProps) return null;

  const {
    hasBurrito = false,
    title,
    lead,
    items,
    contactBox,
    backgroundColor,
    textColor,
    gridItemSize,
    gridItemPush,
  } = faqProps;

  return (
    <>
      <StyledFaq
        className="m-faq"
        backgroundColor={!hasBurrito ? backgroundColor : undefined}
      >
        <StyledGrid columns={{ zero: 'full', large: 12 }}>
          <GridItem
            size={
              gridItemSize || {
                zero: 'full',
                large: !hasBurrito ? 12 : 7,
                ultra: !hasBurrito ? 8 : 7,
              }
            }
            push={gridItemPush || { zero: 0, ultra: !hasBurrito ? 2 : 0 }}
          >
            <StyledBurrito hasBackground={hasBurrito} spacingCurve="peta">
              <ModuleHeader
                title={title || ''}
                lead={lead || undefined}
                color={textColor}
              />
              <Accordion allowMultipleExpanded allowItemCollapse>
                {items.map(
                  (
                    item: {
                      question: string;
                      richtext: ReactNode;
                    },
                    rowIndex: number,
                  ) => {
                    return (
                      <StyledAccordionItem
                        hasBackground={hasBurrito}
                        variant={textColor}
                        key={rowIndex}
                      >
                        <AccordionItemTrigger>
                          {item.question}
                        </AccordionItemTrigger>
                        <AccordionItemContent>
                          {item.richtext}
                        </AccordionItemContent>
                      </StyledAccordionItem>
                    );
                  },
                )}
              </Accordion>
            </StyledBurrito>
          </GridItem>
          {contactBox ? (
            <GridItem
              size={{
                zero: 'full',
                large: !hasBurrito ? 12 : 4,
                ultra: !hasBurrito ? 8 : 4,
              }}
              push={{
                zero: 0,
                large: !hasBurrito ? 0 : 1,
                ultra: !hasBurrito ? 2 : 1,
              }}
            >
              <ContactBox
                withBurrito={hasBurrito}
                linkText={!hasBurrito ? contactBox.labels.more : undefined}
                link={{
                  href: contactBox.link ?? '',
                  title: 'Faq',
                }}
                header={contactBox.labels.title}
                text={contactBox.labels.text}
                button1={
                  contactBox.labels.mail && contactBox.button1
                    ? {
                        text: contactBox.labels.mail,
                        href: contactBox.button1,
                      }
                    : undefined
                }
                button2={
                  contactBox.labels.phone && contactBox.button2
                    ? {
                        text: contactBox.labels.phone,
                        href: contactBox.button2,
                      }
                    : undefined
                }
              />
            </GridItem>
          ) : null}
        </StyledGrid>
      </StyledFaq>
    </>
  );
};

export type FaqProps = {
  items: { question: string; richtext: ReactNode }[];
  title?: string;
  lead?: string;
  hasBurrito?: boolean;
  backgroundColor?: string;
  textColor?: 'light';
  contactBox?: {
    link?: string;
    button1?: string;
    button2?: string;
    labels: {
      title: string;
      text: string;
      mail?: string;
      phone?: string;
      more?: string;
    };
    variant?: string;
  };
  gridItemSize?: { [key: LayoutProp['name']]: number };
  gridItemPush?: { [key: LayoutProp['name']]: number };
} | null;

export default Faq;
